import { useState, useEffect, useRef } from 'react'
import { HomePage } from '@/components/dashboard/HomePage'
import { EntryForm } from '@/components/exchange/EntryForm'
import { StatForm } from '@/components/exchange/StatForm'
import { NoteForm } from '@/components/exchange/NoteForm'
import { StatSummary } from '@/components/exchange/StatSummary'
import { NoteSummary } from '@/components/exchange/NoteSummary'
import { NotesConsultation } from '@/components/notes/NotesConsultation'
import { NotesStatsConsultation } from '@/components/notes/NotesStatsConsultation'
import { TenantNotes } from '@/components/notes/TenantNotes'
import { EditNote } from '@/components/notes/EditNote'
import { ExchangeStep2 } from '@/components/exchange/ExchangeStep2'
import { ExchangeStep3 } from '@/components/exchange/ExchangeStep3'
import { ExchangeStep4 } from '@/components/exchange/ExchangeStep4'
import { ModificationExchange } from '@/components/exchange/ModificationExchange'
import { CsvUploader } from '@/components/exchange/CsvUploader'
import { DataAnalysis } from '@/components/dashboard/DataAnalysis'
import { ManageTeam } from '@/components/dashboard/ManageTeam'
import { LegacyNoteImporter } from '@/components/exchange/LegacyNoteImporter'
import { ClearTestData } from '@/components/exchange/ClearTestData'
import { DerniersContacts } from '@/components/dashboard/DerniersContacts'
import { BeneficiairesList } from '@/components/beneficiaires/BeneficiairesList'
import { BeneficiaireDetail } from '@/components/beneficiaires/BeneficiaireDetail'
import { BeneficiairesOverview } from '@/components/beneficiaires/BeneficiairesOverview'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/contexts/AuthContext'
import { HeaderActionsContext } from '@/contexts/HeaderActionsContext'
import { AppDataProvider } from '@/contexts/AppDataContext'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { statPostprocess } from '@/lib/statScores'
import { IS_DEMO_MODE } from '@/config/config'

const CREATION_PAGES = new Set([
    'nouvelle_entree', 'nouvelle_stat', 'stat_cvc', 'stat_hestia', 'stat_hestia2', 'stat_summary',
    'nouvelle_note', 'note_summary',
    'nouvel_echange_1',
])

// Type definitions for form data
interface EntryData {
    locataire?: string
    beneficiary_id?: string
    travailleur?: string
    worker_id?: string
    collegue?: string[]
    colleague_ids?: string[]
    date?: string
    nouvelle_stat?: boolean
    nouvelle_note?: boolean
}

interface StatData {
    uuid?: string
    locataire?: string
    beneficiary_id?: string
    travailleur?: string
    worker_id?: string
    collegue?: string[]
    colleague_ids?: string[]
    date?: string
    duree?: number
    format_echange?: string
    contenu_echange?: string
    contenu_echange_real?: string[]
    distribution_kit_rdr?: string
    presence?: string
    entree_dans_appartement?: string
    note?: string
    comment?: string
    submit_ts?: string
}

interface NoteData {
    uuid?: string
    locataire?: string
    beneficiary_id?: string
    travailleur?: string
    worker_id?: string
    collegue?: string[]
    colleague_ids?: string[]
    date?: string
    note?: string
    comment?: string
    submit_ts?: string
    mentionedWorkerIds?: string[]
    mentionedWorkerNames?: string[]
}

interface ExchangeInfo {
    uuid?: string
    locataire?: string
    travailleur?: string
    collegue?: string[]
    date?: string
    duree?: number
    format_echange?: string
    contenu_echange?: string
    contenu_echange_real?: string[]
    presence?: string
    entree_dans_appartement?: string
    note?: string
    submit_ts?: string
}

interface CVCData {
    corps?: string
    vetements?: string
    comportement?: string
}

interface HestiaData {
    voisinage?: string
    hygiene?: string
    bailleur?: string
}

interface Hestia2Data {
    securite?: string
    sante?: string
    consommation?: string
}

interface FormData {
    entryData?: EntryData
    statData?: StatData
    noteData?: NoteData
    // Keep the old exchange data for backward compatibility
    info?: ExchangeInfo
    cvc?: CVCData | null
    hestia?: HestiaData | null
    hestia2?: Hestia2Data | null
}

export const DashboardPage = () => {
    const [currentPage, setCurrentPage] = useState('home')
    const [showConfirm, setShowConfirm] = useState(false)
    const [showBrowserBackConfirm, setShowBrowserBackConfirm] = useState(false)
    const [pendingBackPage, setPendingBackPage] = useState<string | null>(null)
    const [browserBackConfirmCopy, setBrowserBackConfirmCopy] = useState<{ title: string; message: string } | null>(null)
    const currentPageRef = useRef('home')
    const isRestoringFromPop = useRef(false)
    const [isEditMode, setIsEditMode] = useState(false)
    const [editingExchangeId, setEditingExchangeId] = useState<string | null>(null)
    const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null)
    const [selectedTenantName, setSelectedTenantName] = useState<string>('')
    const [editingNoteId, setEditingNoteId] = useState<string | null>(null)
    const [editingNoteData, setEditingNoteData] = useState<any>(null)
    const [highlightNoteId, setHighlightNoteId] = useState<string | null>(null)
    const [highlightExchangeId, setHighlightExchangeId] = useState<string | null>(null)
    const [autoOpenBeneficiaryId, setAutoOpenBeneficiaryId] = useState<string | null>(null)
    const [consultationInitialTab, setConsultationInitialTab] = useState<'notes' | 'stats'>('notes')
    const [selectedBeneficiaireId, setSelectedBeneficiaireId] = useState<string | null>(null)
    const [beneficiaireReturnPage, setBeneficiaireReturnPage] = useState<string>('beneficiaires')
    const [linkedExchangeId, setLinkedExchangeId] = useState<string | null>(null)
    const linkedExchangeIdRef = useRef<string | null>(null)
    const { user, userProfile } = useAuth()

    // Form state
    const [formData, setFormData] = useState<FormData>({
        entryData: undefined,
        statData: undefined,
        noteData: undefined,
        info: {},
        cvc: null,
        hestia: null,
        hestia2: null
    })

    const resetForm = () => {
        setFormData({
            entryData: undefined,
            statData: undefined,
            noteData: undefined,
            info: {},
            cvc: null,
            hestia: null,
            hestia2: null
        })
        setIsEditMode(false)
        setEditingExchangeId(null)
        setLinkedExchangeId(null)
    }

    // Keep ref in sync so popstate handler always sees current page
    useEffect(() => { currentPageRef.current = currentPage }, [currentPage])
    useEffect(() => { linkedExchangeIdRef.current = linkedExchangeId }, [linkedExchangeId])

    // Push a history entry on every page change
    useEffect(() => {
        if (isRestoringFromPop.current) {
            isRestoringFromPop.current = false
            return
        }
        window.history.pushState({ page: currentPage }, '')
    }, [currentPage])

    // Browser back/forward support
    useEffect(() => {
        window.history.replaceState({ page: 'home' }, '')

        const handlePopState = (e: PopStateEvent) => {
            const targetPage: string = e.state?.page ?? 'home'
            const cur = currentPageRef.current
            if (CREATION_PAGES.has(cur)) {
                // Undo the browser navigation so we stay on current page if user cancels
                window.history.pushState({ page: cur }, '')
                setPendingBackPage(targetPage)
                if (cur === 'nouvelle_note' && linkedExchangeIdRef.current) {
                    setBrowserBackConfirmCopy({
                        title: 'Quitter la saisie de la note ?',
                        message:
                            'La stat a déjà été enregistrée. Seule la note en cours de rédaction sera perdue si vous continuez.',
                    })
                } else {
                    setBrowserBackConfirmCopy(null)
                }
                setShowBrowserBackConfirm(true)
            } else {
                isRestoringFromPop.current = true
                setCurrentPage(targetPage)
            }
        }

        window.addEventListener('popstate', handlePopState)
        return () => window.removeEventListener('popstate', handlePopState)
    }, [])

    const navigateHome = () => {
        const hasData = formData.entryData || formData.statData || formData.noteData ||
            (formData.info && Object.keys(formData.info).length > 0)

        if (currentPage !== 'home' && hasData) {
            setShowConfirm(true)
        } else {
            setCurrentPage('home')
            resetForm()
        }
    }

    const confirmNavigateHome = () => {
        setShowConfirm(false)
        setCurrentPage('home')
        resetForm()
    }

    const cancelConfirm = () => {
        setShowConfirm(false)
    }

    const handleEditExchange = (exchangeData: any) => {
        // Convert exchange data to StatData format for direct stats form navigation
        const statData: StatData = {
            uuid: exchangeData.info.uuid,
            locataire: exchangeData.info.locataire,
            beneficiary_id: exchangeData.beneficiary_id,
            travailleur: exchangeData.info.travailleur,
            worker_id: exchangeData.worker_id,
            collegue: exchangeData.info.collegue,
            colleague_ids: exchangeData.colleague_ids,
            date: exchangeData.info.date,
            duree: exchangeData.info.duree,
            format_echange: exchangeData.info.format_echange,
            contenu_echange: exchangeData.info.contenu_echange,
            contenu_echange_real: exchangeData.info.contenu_echange_real,
            presence: exchangeData.info.presence,
            entree_dans_appartement: exchangeData.info.entree_dans_appartement,
            note: exchangeData.info.note,
            submit_ts: exchangeData.info.submit_ts
        }

        // Set form data with both the original structure and the converted stat data
        setFormData({
            ...exchangeData,
            statData: statData
        })

        setIsEditMode(true)
        setEditingExchangeId(exchangeData.info.uuid)

        // Navigate directly to stats form instead of the old exchange flow
        setCurrentPage('nouvelle_stat')
    }

    // Navigation handlers
    const handleNavigate = (page: string) => {
        setCurrentPage(page)
    }

    // Entry form handler
    const handleEntrySubmit = (entryData: EntryData) => {
        setFormData(prev => ({ ...prev, entryData }))

        // If stat is checked, start with stat flow (note will follow if also checked)
        if (entryData.nouvelle_stat) {
            setCurrentPage('nouvelle_stat')
        } else if (entryData.nouvelle_note) {
            setCurrentPage('nouvelle_note')
        }
    }

    // Stat form handlers
    const handleStatSubmit = (statData: StatData) => {
        setFormData(prev => ({ ...prev, statData }))

        if (statData.presence === "Présent.e") {
            setCurrentPage('stat_cvc')
        } else if (statData.entree_dans_appartement === "Oui") {
            setCurrentPage('stat_hestia')
        } else {
            // Clear hestia scores if user unchecked "rentré dans l'appartement"
            setFormData(prev => ({ ...prev, statData, hestia: null, hestia2: null }))
            setCurrentPage('stat_summary')
        }
    }

    const handleStatBack = () => {
        setCurrentPage('nouvelle_entree')
    }

    // Note form handlers
    const handleNoteSubmit = (noteData: NoteData) => {
        setFormData(prev => ({ ...prev, noteData }))
        setCurrentPage('note_summary')
    }

    // Stat CVC handlers
    const handleStatCVCSubmit = (cvc: any) => {
        setFormData(prev => ({ ...prev, cvc }))

        if (formData.statData?.entree_dans_appartement === "Oui") {
            setCurrentPage('stat_hestia')
        } else {
            // Clear hestia scores if not entered the apartment
            setFormData(prev => ({ ...prev, cvc, hestia: null, hestia2: null }))
            setCurrentPage('stat_summary')
        }
    }

    const handleStatCVCBack = () => {
        setCurrentPage('nouvelle_stat')
    }

    // Stat HESTIA handlers
    const handleStatHestiaSubmit = (hestia: any) => {
        setFormData(prev => ({ ...prev, hestia }))
        setCurrentPage('stat_hestia2')
    }

    const handleStatHestiaBack = () => {
        setCurrentPage(formData.statData?.presence === "Présent.e" ? 'stat_cvc' : 'nouvelle_stat')
    }

    // Stat HESTIA2 handlers
    const handleStatHestia2Submit = (hestia2: any) => {
        setFormData(prev => ({ ...prev, hestia2 }))
        setCurrentPage('stat_summary')
    }

    const handleStatHestia2Back = () => {
        setCurrentPage('stat_hestia')
    }

    // Stat summary handlers
    const handleStatSummaryEdit = (comment: string) => {
        setFormData(prev => ({
            ...prev,
            statData: { ...prev.statData!, comment }
        }))
        setCurrentPage('nouvelle_stat')
    }

    const handleStatSummarySubmit = async (comment: string) => {
        // The demo saves nothing: move on as if the exchange had been saved.
        if (IS_DEMO_MODE) {
            if (formData.entryData?.nouvelle_note) {
                setFormData(prev => ({ ...prev, statData: undefined, cvc: null, hestia: null, hestia2: null }))
                setCurrentPage('nouvelle_note')
            } else {
                resetForm()
                setCurrentPage('home')
            }
            return
        }
        if (!user?.id) {
            throw new Error('Session expirée. Vos données sont conservées — reconnectez-vous dans un autre onglet puis réessayez.')
        }

        // Force token refresh before insert — prevents JWT expired errors on mobile after background/sleep
        const { error: sessionError } = await supabase.auth.refreshSession()
        if (sessionError) {
            throw new Error('Session expirée. Vos données sont conservées — reconnectez-vous dans un autre onglet puis réessayez.')
        }

        const submitData = {
            ...formData.statData!,
            comment,
            submit_ts: new Date().toISOString()
        }

        try {
            const beneficiaryId = formData.entryData?.beneficiary_id || formData.statData?.beneficiary_id
            const workerId = formData.entryData?.worker_id || formData.statData?.worker_id
            const colleagueIds = formData.entryData?.colleague_ids || formData.statData?.colleague_ids || []

            if (!beneficiaryId) {
                throw new Error('Beneficiary not found')
            }
            if (!workerId) {
                throw new Error('Worker not found')
            }

            // Prepare the stat data for insertion
            const statData = {
                beneficiary_id: beneficiaryId,
                worker_id: workerId,
                colleague_ids: colleagueIds.length > 0 ? colleagueIds : null,
                created_by: user.id,

                // Basic fields
                stat_date: submitData.date!,
                duree: submitData.duree,
                format_echange: submitData.format_echange,
                contenu_echange: submitData.contenu_echange,
                contenu_echange_real: submitData.contenu_echange_real?.join(', ') || null,
                distribution_kit_rdr: submitData.distribution_kit_rdr,
                presence: submitData.presence,
                entree_dans_appartement: submitData.entree_dans_appartement,
                note: submitData.note || null,
                comment: comment || null,
                submit_ts: submitData.submit_ts,

                // CVC fields
                cvc_corps: formData.cvc?.corps || null,
                cvc_vetements: formData.cvc?.vetements || null,
                cvc_comportement: formData.cvc?.comportement || null,

                // HESTIA fields
                hestia_voisinage: formData.hestia?.voisinage || null,
                hestia_hygiene: formData.hestia?.hygiene || null,
                hestia_bailleur: formData.hestia?.bailleur || null,

                // HESTIA 2 fields
                hestia2_securite: formData.hestia2?.securite || null,
                hestia2_sante: formData.hestia2?.sante || null,
                hestia2_consommation: formData.hestia2?.consommation || null,

                // Postprocess fields and numeric scores
                ...statPostprocess({
                    cvc: formData.cvc,
                    hestia: formData.hestia,
                    hestia2: formData.hestia2,
                    colleagueCount: colleagueIds.length,
                }),
            }

            // Insert the stat into the database
            const { data, error } = await supabase
                .from('stats')
                .insert([statData])
                .select()
                .single()

            if (error) {
                console.error('Error inserting stat:', error)
                throw error
            }

            console.log('Stat successfully inserted:', data)

            // If a note was also requested, go to note flow next (preserving entryData)
            if (formData.entryData?.nouvelle_note) {
                setLinkedExchangeId(data.id)
                setFormData(prev => ({
                    ...prev,
                    statData: undefined,
                    cvc: null,
                    hestia: null,
                    hestia2: null
                }))
                setCurrentPage('nouvelle_note')
            } else {
                // Reset form and go home
                resetForm()
                setCurrentPage('home')
            }
        } catch (error) {
            console.error('Error submitting stat:', error)
            throw error
        }
    }

    // Note summary handlers
    const handleNoteSummaryEdit = () => {
        setCurrentPage('nouvelle_note')
    }

    const handleNoteSummarySubmit = async (notificationMessage: string) => {
        // The demo saves nothing, mention notifications included.
        if (IS_DEMO_MODE) {
            setLinkedExchangeId(null)
            resetForm()
            setCurrentPage('home')
            return
        }
        if (!user?.id) {
            throw new Error('Session expirée. Vos données sont conservées — reconnectez-vous dans un autre onglet puis réessayez.')
        }

        // Force token refresh before insert — prevents JWT expired errors on mobile after background/sleep
        const { error: sessionError } = await supabase.auth.refreshSession()
        if (sessionError) {
            throw new Error('Session expirée. Vos données sont conservées — reconnectez-vous dans un autre onglet puis réessayez.')
        }

        const submitData = {
            ...formData.noteData!,
            submit_ts: new Date().toISOString()
        }

        try {
            const beneficiaryId = formData.entryData?.beneficiary_id || formData.noteData?.beneficiary_id
            const workerId = formData.entryData?.worker_id || formData.noteData?.worker_id
            const colleagueIds = formData.entryData?.colleague_ids || formData.noteData?.colleague_ids || []

            if (!beneficiaryId) {
                throw new Error('Beneficiary not found')
            }
            if (!workerId) {
                throw new Error('Worker not found')
            }

            // Prepare the note data for insertion
            const noteData = {
                beneficiary_id: beneficiaryId,
                worker_id: workerId,
                colleague_ids: colleagueIds.length > 0 ? colleagueIds : null,
                created_by: user.id,

                note_date: submitData.date!,
                note: submitData.note!,
                type: submitData.type || null,
                comment: null,
                submit_ts: submitData.submit_ts,
                linked_exchange_id: linkedExchangeId || null,
            }

            // Insert the note into the database
            const { data, error } = await supabase
                .from('notes')
                .insert([noteData])
                .select()
                .single()

            if (error) {
                console.error('Error inserting note:', error)
                throw error
            }

            console.log('Note successfully inserted:', data)

            // Insert in-app notifications for mentioned workers
            const mentionedWorkerIds = formData.noteData?.mentionedWorkerIds
            if (mentionedWorkerIds && mentionedWorkerIds.length > 0) {
                try {
                    const senderWorkerId = userProfile?.worker_id || null
                    const senderName = submitData.travailleur || 'Un.e collègue'
                    const locataireName = submitData.locataire || ''

                    const notifRows = mentionedWorkerIds
                        .map((id: string) => ({
                            worker_id: id,                  // recipient
                            mentioned_by: senderWorkerId,   // sender
                            item_id: data.id,               // note id
                            table_name: 'notes',
                            notification_type: 'mention',
                            created_by: userProfile?.id || null,
                            metadata: {
                                beneficiary_id: beneficiaryId,
                                locataire: locataireName,
                                sender_name: senderName,
                                message: notificationMessage.trim() || null
                            }
                        }))

                    if (notifRows.length > 0) {
                        const { error: notifError } = await supabase
                            .from('notifications')
                            .insert(notifRows)
                        if (notifError) {
                            console.error('Error inserting notifications:', notifError)
                        }
                    }
                } catch (notifError) {
                    console.error('Error sending notifications:', notifError)
                    // Non-critical — note is saved
                }
            }

            // Reset form and go home
            setLinkedExchangeId(null)
            resetForm()
            setCurrentPage('home')
        } catch (error) {
            console.error('Error submitting note:', error)
            throw error
        }
    }

    // Notes consultation handlers
    const handleNavigateToTenantNotes = (tenantId: string, tenantName: string) => {
        setSelectedTenantId(tenantId)
        setSelectedTenantName(tenantName)
        setCurrentPage('tenant_notes')
    }

    const handleBackToNotesConsultation = () => {
        setSelectedTenantId(null)
        setSelectedTenantName('')
        setHighlightExchangeId(null)
        setAutoOpenBeneficiaryId(null)
        setConsultationInitialTab('notes')
        setCurrentPage('consultation_notes_stats')
    }

    const handleEditNote = (noteId: string, noteData: any) => {
        setEditingNoteId(noteId)
        setEditingNoteData(noteData)
        setCurrentPage('edit_note')
    }

    const handleBackToTenantNotes = () => {
        setEditingNoteId(null)
        setEditingNoteData(null)
        if (selectedTenantId) {
            setCurrentPage('tenant_notes')
        } else {
            setCurrentPage('consultation_notes_stats')
        }
    }

    const handleNavigateToNote = async (noteId: string, beneficiaryId: string) => {
        try {
            const { data: ben } = await supabase
                .from('beneficiaries')
                .select('name')
                .eq('id', beneficiaryId)
                .maybeSingle()
            setSelectedTenantId(beneficiaryId)
            setSelectedTenantName(ben?.name || '')
            setHighlightNoteId(noteId)
            setCurrentPage('tenant_notes')
        } catch {
            setSelectedTenantId(beneficiaryId)
            setSelectedTenantName('')
            setHighlightNoteId(noteId)
            setCurrentPage('tenant_notes')
        }
    }

    const handleNavigateToExchange = (exchangeId: string, beneficiaryId: string) => {
        setHighlightExchangeId(exchangeId)
        setAutoOpenBeneficiaryId(beneficiaryId)
        setConsultationInitialTab('stats')
        setCurrentPage('consultation_notes_stats')
    }

    const handleNoteSaved = () => {
        // Clear editing state and return to tenant notes
        setEditingNoteId(null)
        setEditingNoteData(null)
        if (selectedTenantId) {
            setCurrentPage('tenant_notes')
        } else {
            setCurrentPage('consultation_notes_stats')
        }
    }

    // Common props for all components
    const commonProps = {
        formData,
        onNavigateHome: navigateHome,
        showConfirm,
        onConfirmNavigateHome: confirmNavigateHome,
        onCancelConfirm: cancelConfirm,
        isEditMode
    }

    // Main render logic
    const renderPage = () => { switch (currentPage) {
        case 'home':
            return (
                <HomePage
                    onNavigate={handleNavigate}
                    onShowConfirm={() => setShowConfirm(true)}
                    {...commonProps}
                />
            )

        case 'nouvelle_entree':
        case 'nouvel_echange_1':
            return (
                <EntryForm
                    onSubmit={handleEntrySubmit}
                    {...commonProps}
                />
            )

        case 'nouvelle_stat':
            return (
                <StatForm
                    onSubmit={handleStatSubmit}
                    onBack={handleStatBack}
                    {...commonProps}
                />
            )

        case 'nouvelle_note':
            return (
                <NoteForm
                    onSubmit={handleNoteSubmit}
                    confirmNavigateHomeCopy={
                        linkedExchangeId
                            ? {
                                  title: "Revenir à l'accueil ?",
                                  message:
                                      'La stat a déjà été enregistrée. Seule la note en cours de rédaction sera perdue.',
                              }
                            : undefined
                    }
                    {...commonProps}
                />
            )

        case 'stat_cvc':
            return (
                <ExchangeStep2
                    onSubmit={handleStatCVCSubmit}
                    onBack={handleStatCVCBack}
                    {...commonProps}
                />
            )

        case 'stat_hestia':
            return (
                <ExchangeStep3
                    onSubmit={handleStatHestiaSubmit}
                    onBack={handleStatHestiaBack}
                    {...commonProps}
                />
            )

        case 'stat_hestia2':
            return (
                <ExchangeStep4
                    onSubmit={handleStatHestia2Submit}
                    onBack={handleStatHestia2Back}
                    {...commonProps}
                />
            )

        case 'stat_summary':
            return (
                <StatSummary
                    onEdit={handleStatSummaryEdit}
                    onSubmit={handleStatSummarySubmit}
                    {...commonProps}
                />
            )

        case 'note_summary':
            return (
                <NoteSummary
                    onEdit={handleNoteSummaryEdit}
                    onSubmit={(msg) => handleNoteSummarySubmit(msg)}
                    {...commonProps}
                />
            )

        case 'consultation_notes_stats':
            return (
                <NotesStatsConsultation
                    onNavigateHome={() => { setConsultationInitialTab('notes'); setCurrentPage('home') }}
                    onNavigateToTenantNotes={handleNavigateToTenantNotes}
                    onNavigateToEdit={handleEditExchange}
                    onNavigateToBeneficiaire={(id) => { setSelectedBeneficiaireId(id); setBeneficiaireReturnPage('consultation_notes_stats'); setCurrentPage('beneficiaire_detail') }}
                    initialTab={consultationInitialTab}
                    highlightExchangeId={highlightExchangeId || undefined}
                    autoOpenBeneficiaryId={autoOpenBeneficiaryId || undefined}
                />
            )

        case 'consultation_notes':
            setConsultationInitialTab('notes')
            setCurrentPage('consultation_notes_stats')
            return null

        case 'tenant_notes':
            return selectedTenantId ? (
                <TenantNotes
                    tenantId={selectedTenantId}
                    tenantName={selectedTenantName}
                    onNavigateBack={handleBackToNotesConsultation}
                    onEditNote={handleEditNote}
                    highlightNoteId={highlightNoteId || undefined}
                    onNavigateToExchange={handleNavigateToExchange}
                />
            ) : (
                <NotesStatsConsultation
                    onNavigateHome={() => setCurrentPage('home')}
                    onNavigateToTenantNotes={handleNavigateToTenantNotes}
                    onNavigateToEdit={handleEditExchange}
                    initialTab="notes"
                />
            )

        case 'edit_note':
            return editingNoteId && editingNoteData ? (
                <EditNote
                    noteId={editingNoteId}
                    noteData={editingNoteData}
                    onBack={handleBackToTenantNotes}
                    onSave={handleNoteSaved}
                    onNavigateHome={() => setCurrentPage('home')}
                    showConfirm={showConfirm}
                    onConfirmNavigateHome={confirmNavigateHome}
                    onCancelConfirm={cancelConfirm}
                />
            ) : (
                <NotesStatsConsultation
                    onNavigateHome={() => setCurrentPage('home')}
                    onNavigateToTenantNotes={handleNavigateToTenantNotes}
                    onNavigateToEdit={handleEditExchange}
                    initialTab="notes"
                />
            )

        case 'modification_echange':
            setConsultationInitialTab('stats')
            setCurrentPage('consultation_notes_stats')
            return null

        case 'csv_uploader':
            return (
                <CsvUploader
                    onNavigateHome={() => setCurrentPage('home')}
                />
            )

        case 'data_analysis':
            return (
                <DataAnalysis
                    onNavigate={(route) => setCurrentPage(route)}
                    onNavigateToBeneficiaire={(id) => { setSelectedBeneficiaireId(id); setBeneficiaireReturnPage('data_analysis'); setCurrentPage('beneficiaire_detail') }}
                />
            )

        case 'manage_team':
            return (
                <ManageTeam
                    onNavigateHome={() => setCurrentPage('home')}
                />
            )

        case 'legacy_notes':
            return (
                <LegacyNoteImporter
                    onNavigateHome={() => setCurrentPage('home')}
                />
            )

        case 'clear_test_data':
            return (
                <ClearTestData
                    onNavigateHome={() => setCurrentPage('home')}
                />
            )

        case 'derniers_contacts':
            setCurrentPage('data_analysis')
            return null

        case 'beneficiaires':
            return (
                <BeneficiairesList
                    onNavigateHome={() => setCurrentPage('home')}
                    onSelectBeneficiaire={(id) => {
                        setSelectedBeneficiaireId(id)
                        setBeneficiaireReturnPage('beneficiaires')
                        setCurrentPage('beneficiaire_detail')
                    }}
                    onOverview={() => setCurrentPage('beneficiaires_overview')}
                />
            )

        case 'beneficiaires_overview':
            return (
                <BeneficiairesOverview
                    onNavigateBack={() => setCurrentPage('beneficiaires')}
                    onSelectBeneficiaire={(id) => {
                        setSelectedBeneficiaireId(id)
                        setBeneficiaireReturnPage('beneficiaires_overview')
                        setCurrentPage('beneficiaire_detail')
                    }}
                />
            )

        case 'beneficiaire_detail':
            return selectedBeneficiaireId ? (
                <BeneficiaireDetail
                    beneficiaireId={selectedBeneficiaireId}
                    onBack={() => setCurrentPage(beneficiaireReturnPage)}
                />
            ) : (
                <BeneficiairesList
                    onNavigateHome={() => setCurrentPage('home')}
                    onSelectBeneficiaire={(id) => {
                        setSelectedBeneficiaireId(id)
                        setBeneficiaireReturnPage('beneficiaires')
                        setCurrentPage('beneficiaire_detail')
                    }}
                />
            )

        default:
            return (
                <HomePage
                    onNavigate={handleNavigate}
                    onShowConfirm={() => setShowConfirm(true)}
                    {...commonProps}
                />
            )
    }}

    return (
        <AppDataProvider>
        <HeaderActionsContext.Provider value={{ onNavigate: handleNavigate, onNavigateToNote: handleNavigateToNote }}>
            {renderPage()}
            {showBrowserBackConfirm && (
                <ConfirmDialog
                    title={browserBackConfirmCopy?.title ?? 'Quitter le formulaire ?'}
                    message={
                        browserBackConfirmCopy?.message ?? 'Les données saisies seront perdues.'
                    }
                    onConfirm={() => {
                        setShowBrowserBackConfirm(false)
                        const target = pendingBackPage ?? 'home'
                        setPendingBackPage(null)
                        setBrowserBackConfirmCopy(null)
                        if (!CREATION_PAGES.has(target)) resetForm()
                        setCurrentPage(target)
                    }}
                    onCancel={() => {
                        setShowBrowserBackConfirm(false)
                        setPendingBackPage(null)
                        setBrowserBackConfirmCopy(null)
                    }}
                />
            )}
        </HeaderActionsContext.Provider>
        </AppDataProvider>
    )
} 